import { Capacitor } from "@capacitor/core";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import React, { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useLocalizedWeekDays } from "../../hooks/useDateLocalization";
import { useEffectiveWeekStartDay } from "../../hooks/useEffectiveWeekStartDay";
import { useElementHeight } from "../../hooks/useElementHeight";
import { INK, NEUTRAL, SHIFT_DAY, SHIFT_NIGHT_FALLBACK, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { formatHoursMinutes, getShiftDisplayDate, getWeekStart } from "../../utils/dateUtils";
import { PtoTypes, RequestResponsesStatusOptions, type SelectedShift, ShiftType, type TransformedShift } from "../../utils/models";
import { isBedSideNurse } from "../../utils/utilFunctions";
import { useSwipeHintLastTip } from "./useSwipeHintLastTip";

const MotionBox = motion.create(Box);

type DateShiftMapValue = {
	date: DateTime;
	hasDayRequest: boolean;
	hasNightRequest: boolean;
	shiftType?: ShiftType;
	isTimeOff?: boolean;
} & Partial<TransformedShift>;

interface CustomCalendarProps {
	shifts: TransformedShift[];
	value?: DateTime;
	onChange?: (date: DateTime) => void;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	showOneWeek?: boolean;
	onShiftInfo?: (selectedShift: SelectedShift) => void;
	showTitle?: boolean;
	onRootHeightChange?: (height: number) => void;
	allowWeeksExpansion?: boolean;
	totalWeeksExpansionAmount?: number;
}

export const CustomCalendar: FC<CustomCalendarProps> = ({
	shifts,
	value = DateTime.now(),
	onChange,
	isOpen,
	onOpenChange,
	showOneWeek = false,
	onShiftInfo,
	showTitle,
	onRootHeightChange,
	allowWeeksExpansion = false,
	totalWeeksExpansionAmount = 4,
}) => {
	const { t } = useTranslation();
	const weekStartDay = useEffectiveWeekStartDay();
	const [expandedWeeks, setExpandedWeeks] = useState(false);
	const [internalStartDate, setInternalStartDate] = useState<DateTime>(getWeekStart(value, weekStartDay));
	const [touchStart, setTouchStart] = useState<number | null>(null);
	const [localOpen, setLocalOpen] = useState(isOpen);
	const [isManualInteraction, setIsManualInteraction] = useState<boolean>(false);
	const calendarRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const lastScrollTop = useRef(0);
	const isUserScrolling = useRef(false);
	const clickProtectionTimeout = useRef<ReturnType<typeof setTimeout>>(null);
	const { userInfo } = useAuth();
	const { hospitalData, chosenDepartment } = useHospitalData();
	const { mergedRequests } = useRequestsContext();
	const rootRef = useRef<HTMLDivElement>(null);
	const height = useElementHeight(rootRef);
	const weekDays = useLocalizedWeekDays();
	const { isHintAvailable } = useSwipeHintLastTip();

	const toggleExpandedWeeks = () => {
		setExpandedWeeks((prev) => !prev);
	};

	useEffect(() => {
		if (height > 0) onRootHeightChange?.(height);
	}, [height, onRootHeightChange]);

	const isNative = Capacitor.isNativePlatform();

	const dateShiftMap = useMemo(() => {
		const map: Record<string, DateShiftMapValue> = {};

		shifts.forEach((shift) => {
			const dateStr = shift.date.toFormat("yyyy-MM-dd");
			map[dateStr] = {
				...shift,
				hasDayRequest: false,
				hasNightRequest: false,
				shiftType: shift.shiftType,
				isTimeOff: shift.isTimeOff,
			};
		});

		mergedRequests.forEach((request) => {
			const dateStr = getShiftDisplayDate(request.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat("yyyy-MM-dd");
			if (!map[dateStr]) {
				map[dateStr] = {
					date: request.startTime,
					hasDayRequest: false,
					hasNightRequest: false,
				};
			}
			if (request.type === ShiftType.Day && (request.status === RequestResponsesStatusOptions.PENDING || request.status === "")) {
				map[dateStr].hasDayRequest = true;
			}
			if (request.type === ShiftType.Night && (request.status === RequestResponsesStatusOptions.PENDING || request.status === "")) {
				map[dateStr].hasNightRequest = true;
			}
		});

		return map;
	}, [shifts, mergedRequests, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	useEffect(() => {
		const weekStart = getWeekStart(value, weekStartDay);
		if (!showOneWeek && weekStart.diff(internalStartDate, "days").as("days") === 7) {
			return;
		}
		setInternalStartDate(weekStart);
	}, [value, weekStartDay]);

	useEffect(() => {
		// Update localOpen when isOpen prop changes
		if (isOpen !== undefined) {
			setLocalOpen(isOpen);
			// Add click protection when opening calendar
			if (isOpen) {
				if (clickProtectionTimeout.current) {
					clearTimeout(clickProtectionTimeout.current);
					clickProtectionTimeout.current = null;
				}
				isUserScrolling.current = false;
				clickProtectionTimeout.current = setTimeout(() => {
					isUserScrolling.current = false;
				}, 300);
			}
		}
	}, [isOpen, userInfo?.id, value]);

	useEffect(() => {
		const mainContainer = document.querySelector("main");
		if (!mainContainer) return;

		let isUserInitiatedScroll = false;

		const handleWheelStart = () => {
			isUserInitiatedScroll = true;
		};

		const handleTouchStart = () => {
			isUserInitiatedScroll = true;
		};

		const handleScrollEnd = () => {
			isUserInitiatedScroll = false;
		};

		const handleScroll = () => {
			if (!isUserInitiatedScroll || isManualInteraction) return;

			const currentScrollTop = mainContainer.scrollTop;
			if (currentScrollTop > lastScrollTop.current && currentScrollTop > 10) {
				onOpenChange?.(false);

				// Track auto-close due to scroll
				trackEvent("custom_calendar_auto_closed", {
					user_id: userInfo?.id,
					scroll_position: currentScrollTop,
					current_date: value.toFormat("yyyy-MM-dd"),
				});
			}
			lastScrollTop.current = currentScrollTop;
		};

		mainContainer.addEventListener("wheel", handleWheelStart, {
			passive: true,
		});
		mainContainer.addEventListener("touchstart", handleTouchStart, {
			passive: true,
		});
		mainContainer.addEventListener("touchend", handleScrollEnd, {
			passive: true,
		});
		mainContainer.addEventListener("wheel", handleScrollEnd, {
			once: true,
			passive: true,
		});
		mainContainer.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			mainContainer.removeEventListener("wheel", handleWheelStart);
			mainContainer.removeEventListener("touchstart", handleTouchStart);
			mainContainer.removeEventListener("touchend", handleScrollEnd);
			mainContainer.removeEventListener("wheel", handleScrollEnd);
			mainContainer.removeEventListener("scroll", handleScroll);
		};
	}, [isManualInteraction, onOpenChange, userInfo?.id, value]);

	const handlePrevWeeks = () => {
		const bedsideNurse: boolean = isBedSideNurse(userInfo?.role);
		const weeksToSubtract: number = showOneWeek ? 1 : 2;
		const newDate = internalStartDate.minus({ weeks: weeksToSubtract });

		// For drag to refresh the app on native platforms, apply navigation logic only for bedside nurses, to navigate only one week backward from current week
		if (isNative && bedsideNurse) {
			if (internalStartDate <= getWeekStart(hospitalData.weeks[0].weeklyShifts[0].days[0].date, weekStartDay)) {
				return;
			}
			const firstWeekStartDate = internalStartDate;
			const secondWeekStartDate = internalStartDate.plus({ weeks: 1 });
			if (!firstWeekStartDate) return; // Safety check

			// Get the current week start (normalize to week start for comparison)
			const currentWeekStart = internalStartDate.startOf("week");
			const firstWeekStart = firstWeekStartDate.startOf("week");
			const secondWeekStart = secondWeekStartDate?.startOf("week");

			// Check if current week is the second week in our data
			if (secondWeekStart && currentWeekStart.hasSame(secondWeekStart, "week")) {
				// Option 1: Current week is second week - no backward navigation allowed
				return;
			}

			// Check if current week is the first week in our data
			if (currentWeekStart.hasSame(firstWeekStart, "week")) {
				// Option 2: Current week is first week - allow only one week before
				const oneWeekBefore = firstWeekStart.minus({ weeks: 1 });
				if (newDate < oneWeekBefore.startOf("week")) {
					return; // Don't allow navigation beyond one week before first week
				}
			}
		}

		setInternalStartDate(newDate);

		trackEvent("custom_calendar_navigation", {
			user_id: userInfo?.id,
			direction: "prev",
			weeks_changed: weeksToSubtract,
			previous_start_date: internalStartDate.toFormat("yyyy-MM-dd"),
			new_start_date: newDate.toFormat("yyyy-MM-dd"),
		});
	};

	const handleNextWeeks = () => {
		const weeksToAdd: number = showOneWeek ? 1 : 2;
		const newDate = internalStartDate.plus({ weeks: weeksToAdd });
		setInternalStartDate(newDate);
		trackEvent("custom_calendar_navigation", {
			user_id: userInfo?.id,
			direction: "next",
			weeks_changed: 2,
			previous_start_date: internalStartDate.toFormat("yyyy-MM-dd"),
			new_start_date: newDate.toFormat("yyyy-MM-dd"),
		});
	};

	const handleTouchStart = (e: React.TouchEvent) => {
		setTouchStart(e.touches[0].clientX);
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		if (!touchStart) {
			return;
		}

		const currentTouch = e.touches[0].clientX;
		const diff = touchStart - currentTouch;

		// Minimum swipe distance of 50px
		if (Math.abs(diff) > 50) {
			if (diff > 0) {
				handleNextWeeks();
			} else {
				handlePrevWeeks();
			}
			setTouchStart(null);
		}
	};

	const handleTouchEnd = () => {
		setTouchStart(null);
	};

	const handleDateClick = (date: DateTime) => {
		// Add click protection when selecting a date
		if (clickProtectionTimeout.current) {
			clearTimeout(clickProtectionTimeout.current);
			clickProtectionTimeout.current = null;
		}
		isUserScrolling.current = false;
		clickProtectionTimeout.current = setTimeout(() => {
			isUserScrolling.current = false;
		}, 300);
		onChange?.(date);

		const shiftInfo = dateShiftMap[date.toFormat("yyyy-MM-dd")];

		onShiftInfo?.({
			date: date.toFormat("yyyy-MM-dd"),
			shiftId: shiftInfo?.id,
			shiftType: shiftInfo?.shiftType,
			startTime: shiftInfo?.startTime,
			endTime: shiftInfo?.endTime,
		});
	};

	const renderDays = () => {
		const days = [];

		// Render week days — labels are Sunday-first; rotate so column 0 matches the effective week start.
		const orderedWeekDays = weekDays.map((_, i) => weekDays[(i + weekStartDay) % 7]);
		days.push(
			<Box key="weekdays" sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
				{orderedWeekDays.map((day) => (
					<Typography
						key={day}
						variant="body2"
						sx={{
							textAlign: "center",
							color: alpha(INK.SECONDARY, 0.5),
							py: 0,
							fontSize: "0.75rem",
							fontWeight: 600,
							letterSpacing: "0.02em",
							opacity: 0.8,
						}}
					>
						{day}
					</Typography>
				))}
			</Box>,
		);

		// Use internalStartDate instead of value for generating dates
		const dates = [];
		const numberOfDays = !expandedWeeks ? (showOneWeek ? 7 : 14) : totalWeeksExpansionAmount * 7;

		for (let i = 0; i < numberOfDays; i++) {
			const date = internalStartDate.plus({ day: i });
			dates.push(date);
		}

		const staffScheduleDisplayUntilDate = chosenDepartment.staffScheduleDisplayUntilDate;

		// Split into weeks and render
		for (let i = 0; i < dates.length; i += 7) {
			const week = dates.slice(i, i + 7);
			days.push(
				<Box key={i} sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
					{week.map((date) => {
						const isSelected = value.hasSame(date, "day");
						const dateString = date.toFormat("yyyy-MM-dd");
						const isToday = date.hasSame(DateTime.now(), "day");
						const shiftInfo: DateShiftMapValue = dateShiftMap[dateString];
						const isBlocked = staffScheduleDisplayUntilDate ? date.startOf("day") > staffScheduleDisplayUntilDate.startOf("day") : false;
						return (
							<Box
								key={date.toString()}
								onClick={() => !isBlocked && handleDateClick(date)}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									cursor: isBlocked ? "default" : "pointer",
									position: "relative",
									mb: 0.5,
									touchAction: "none",
									pb: 2,
									...(isBlocked && { opacity: 0.35, pointerEvents: "none" }),
								}}
							>
								<Box
									sx={(theme) => ({
										width: "40px",
										height: "40px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										position: "relative",
										flexDirection: "column",
										fontWeight: 600,
										...(isBlocked
											? {
													borderRadius: "50%",
													color: INK.DISABLED,
												}
											: shiftInfo?.isTimeOff
												? {
														bgcolor: NEUTRAL[100],
														borderRadius: "4px",
														width: "100%",
														height: "100%",
													}
												: {
														borderRadius: "50%",
														color: INK.SECONDARY,
														...(isToday && {
															border: `2px solid ${INK.DISABLED}`,
														}),
														...(shiftInfo?.shiftType === ShiftType.Night && {
															bgcolor: SHIFT_NIGHT_FALLBACK,
															color: SURFACE,
														}),
														...(shiftInfo?.shiftType === ShiftType.Day && {
															bgcolor: SHIFT_DAY,
															color: SURFACE,
														}),
														...(shiftInfo?.shiftType === ShiftType.All && {
															background: `linear-gradient(135deg, ${SHIFT_DAY} 50%, ${SHIFT_NIGHT_FALLBACK} 50%)`,
															color: SURFACE,
														}),
														...(isSelected && {
															bgcolor: alpha(theme.palette.primary.main, 0.2),
															color: INK.SECONDARY,
														}),
													}),
									})}
								>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{date.day}
									</Typography>
									{shiftInfo?.isTimeOff && (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												fontSize: "12px",
												mt: -0.5,
											}}
										>
											<Typography
												variant="caption"
												data-testid={`my-schedule-calendar-time-off-${date.toFormat("yyyy-MM-dd")}`}
												sx={{
													fontSize: "0.6rem",
													color: INK.MUTED,
												}}
											>
												{shiftInfo.timeOffWindow ? formatHoursMinutes(shiftInfo.timeOffWindow.hours) : t("day.off")}
											</Typography>
											{shiftInfo.timeOffReason === PtoTypes.Payed ? (
												<AttachMoneyOutlinedIcon fontSize={"inherit"} />
											) : shiftInfo.timeOffReason === PtoTypes.Unpayed ? (
												<MoneyOffOutlinedIcon fontSize={"inherit"} />
											) : null}
										</Box>
									)}
									{/* Request indicators */}
									<Box
										sx={{
											position: "absolute",
											bottom: "-12px",
											display: "flex",
											gap: "4px",
											justifyContent: "center",
											width: "100%",
										}}
									>
										{shiftInfo?.hasDayRequest && (
											<Box
												sx={{
													width: "8px",
													height: "8px",
													borderRadius: "50%",
													border: `2px solid ${SHIFT_DAY}`,
													backgroundColor: "transparent",
												}}
											/>
										)}
										{shiftInfo?.hasNightRequest && (
											<Box
												sx={{
													width: "8px",
													height: "8px",
													borderRadius: "50%",
													border: `2px solid ${SHIFT_NIGHT_FALLBACK}`,
													backgroundColor: "transparent",
												}}
											/>
										)}
									</Box>
								</Box>
							</Box>
						);
					})}
				</Box>,
			);
		}

		return days;
	};

	// Get the end date for the two-week period
	const endDate = internalStartDate.plus({ days: 13 });
	const dateRangeText =
		internalStartDate.month !== endDate.month
			? `${internalStartDate.toFormat("MMM")} - ${endDate.toFormat("MMM yyyy")}`
			: `${internalStartDate.toFormat("MMM yyyy")}`;

	const handleHeaderClick = () => {
		setIsManualInteraction(true);
		setLocalOpen((prevState) => {
			onOpenChange?.(!prevState);
			return !prevState;
		});

		setTimeout(() => {
			setIsManualInteraction(false);
		}, 300);
	};

	return (
		<Box
			ref={rootRef}
			data-testid="my-schedule-calendar"
			sx={{
				width: "100%",
				maxWidth: 600,
				mx: "auto",
				backgroundColor: SURFACE,
				px: 2,
				position: "sticky",
				top: 0,
				zIndex: 1000,
			}}
		>
			{showTitle && (
				<Typography
					variant="body2"
					sx={{
						fontWeight: 600,
						textAlign: "center",
						color: INK.SECONDARY,
						py: 0,
						fontSize: "1rem",
						letterSpacing: "0.02em",
						opacity: 0.8,
					}}
				>
					{value.toFormat("MMMM")}
				</Typography>
			)}
			{!showOneWeek && (
				<Box
					ref={headerRef}
					onClick={handleHeaderClick}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						py: 0.5,
						cursor: "pointer",
						userSelect: "none",
						gap: 1,
					}}
				>
					<Typography variant="h6" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "1.2rem" }}>
						{dateRangeText}
					</Typography>
					{!localOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
				</Box>
			)}
			<Box
				ref={calendarRef}
				sx={{
					height: showOneWeek ? "auto" : !localOpen ? "0px" : "auto",
					overflow: "hidden",
					transition: showOneWeek ? "none" : "height 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
				}}
			>
				<MotionBox
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					// jiggle once on mount
					initial={{ x: 0 }}
					animate={isHintAvailable ? { x: [0, 12, -12, 8, -8, 0] } : false}
					transition={{
						duration: 0.7,
						ease: "easeInOut",
						delay: 4,
					}}
				>
					{renderDays()}
				</MotionBox>
			</Box>
			{allowWeeksExpansion && localOpen && (
				<Box
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<IconButton size={"small"} onClick={toggleExpandedWeeks}>
						{!expandedWeeks ? <KeyboardArrowDownIcon sx={{ color: INK.SECONDARY }} /> : <KeyboardArrowUpIcon sx={{ color: INK.SECONDARY }} />}
					</IconButton>
				</Box>
			)}
		</Box>
	);
};
